import { useCallback, useSyncExternalStore } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";

/**
 * When the queries under `queryKeys` last heard from the server, or undefined
 * while nothing has loaded.
 *
 * Reads the query cache by key prefix rather than taking a hook's result,
 * because one header control has to speak for every query on its page — and
 * because the lib's cursor `usePaginatedQuery` picks only
 * `{ data, isLoading, isError, error, refetch }` off the inner hook and drops
 * the timing with it.
 *
 * The newest wins, not the oldest: the stamp answers "when did this page last
 * hear from the server". The prefixes cover queries on different tiers — a
 * list watching at ten seconds beside reference data on the minute baseline —
 * and letting the slowest of them set the stamp would report the page as
 * staler than anything on it actually is.
 */
export function useLastUpdatedAt(queryKeys: QueryKey[]): number | undefined {
  const cache = useQueryClient().getQueryCache();

  const subscribe = useCallback(
    (onStoreChange: () => void) => cache.subscribe(onStoreChange),
    [cache],
  );

  // A number, so React's Object.is check keeps re-renders to the moments a
  // fetch actually lands rather than every cache event.
  const getSnapshot = useCallback(() => {
    let latest = 0;
    for (const queryKey of queryKeys) {
      for (const query of cache.findAll({ queryKey, type: "active" })) {
        if (query.state.dataUpdatedAt > latest) latest = query.state.dataUpdatedAt;
      }
    }
    return latest;
  }, [cache, queryKeys]);

  return useSyncExternalStore(subscribe, getSnapshot) || undefined;
}
